import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';

import { Organization as ApiOrganization } from '../../types';
import { Organization as ContextOrganization } from '../../context/OrganizationContext';
import { ApiError } from '../../utils/error-handler';
import { useOrganizationPermissions } from '../../hooks/usePermissions';
import './utils/compatibility-fix';
import { formatDate } from '../../utils/date-formatter';
import ErrorDisplay from '../common/feedback/ErrorDisplay';
import { ledeSx } from '../../theme/styleHelpers';

interface OrganizationHeaderProps {
  organization: ApiOrganization | ContextOrganization;
  onUpdate: (data: { name: string; description?: string }) => Promise<{ success: boolean; error?: ApiError }>;
}

// Helper function to safely get organization properties regardless of interface type
const getOrgProperty = (org: ApiOrganization | ContextOrganization, prop: string) => {
  // Handle API v2 field mapping (e.g., createdOn -> createdAt)
  const value = (org as any)[prop];
  if (value !== undefined && value !== null) {
    return value;
  }
  
  // Fallback mappings for API v2 compatibility
  if (prop === 'createdAt' && (org as any).createdOn) {
    return (org as any).createdOn;
  }
  if (prop === 'updatedAt' && (org as any).updatedOn) {
    return (org as any).updatedOn;
  }
  
  return '';
};

const OrganizationHeader: React.FC<OrganizationHeaderProps> = ({ organization, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    name: getOrgProperty(organization, 'name'),
    description: getOrgProperty(organization, 'description')
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  // Check permissions
  const orgId = (organization as any)._id || (organization as any).id;
  const { canUpdateOrganization } = useOrganizationPermissions(orgId);

  const handleEditClick = () => {
    setEditData({
      name: getOrgProperty(organization, 'name'),
      description: getOrgProperty(organization, 'description')
    });
    setIsEditing(true);
    setError(null);
  };

  const handleCloseEdit = () => {
    setIsEditing(false);
    setError(null);
  };

  const handleChange = (field: 'name' | 'description') => (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditData(prev => ({ ...prev, [field]: e.target.value }));
    if (error) setError(null);
  };

  const handleSubmit = async () => {
    if (!editData.name || !editData.name.trim()) {
      setError({
        message: 'Organization name is required',
        code: 'VALIDATION_ERROR',
        status: 400,
        field: 'name'
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await onUpdate({
        name: editData.name,
        description: editData.description || undefined
      });

      if (result.success) {
        setIsEditing(false);
      } else if (result.error) {
        setError(result.error);
      }
    } catch (err) {
      console.error('Failed to update organization:', err);
      setError({
        message: 'Failed to update organization',
        code: 'UNKNOWN_ERROR',
        status: 500
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const orgName = getOrgProperty(organization, 'name') || 'Untitled Organization';
  const orgDescription = getOrgProperty(organization, 'description');

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, marginBottom: 0.5 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h1" sx={{
            fontSize: 32,
            fontWeight: 800,
            letterSpacing: '-0.025em',
            lineHeight: 1.15,
            marginBottom: 1,
          }}>
            {orgName}
          </Typography>
        </Box>
        {canUpdateOrganization() && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, pt: 0.5 }}>
            <Tooltip title="Edit Organization">
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={handleEditClick}
                size="small"
              >
                Edit
              </Button>
            </Tooltip>
          </Box>
        )}
      </Box>
      <Typography sx={ledeSx}>
        {orgDescription || 'Manage your organization settings, teams, and members.'}
      </Typography>

      {/* Edit Dialog */}
      <Dialog open={isEditing} onClose={handleCloseEdit} maxWidth="sm" fullWidth>
        <DialogTitle>
          Edit Organization
          <IconButton
            aria-label="close"
            onClick={handleCloseEdit}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {error && (
            <Box sx={{ mt: 2 }}>
              <ErrorDisplay error={error} onClose={() => setError(null)} />
            </Box>
          )}

          <TextField
            margin="normal"
            required
            fullWidth
            id="name"
            label="Organization Name"
            name="name"
            value={editData.name}
            onChange={handleChange('name')}
            error={error?.field === 'name'}
            disabled={isSubmitting}
            autoFocus
          />

          <TextField
            margin="normal"
            fullWidth
            id="description"
            label="Description"
            name="description"
            value={editData.description}
            onChange={handleChange('description')}
            error={error?.field === 'description'}
            multiline
            rows={3}
            disabled={isSubmitting}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseEdit} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            disabled={isSubmitting || !editData.name || !editData.name.trim()}
            startIcon={<SaveIcon />}
            variant="contained"
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrganizationHeader;